import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  OnDestroy,
  output,
  signal,
  ViewChild,
} from '@angular/core';

import { BarcodeFormat, BrowserMultiFormatReader, DecodeHintType, NotFoundException } from '@zxing/library';

@Component({
  selector: 'ticketing-frame-scanner',
  imports: [],
  templateUrl: './framed-scanner.component.html',
  styleUrl: './framed-scanner.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FramedScannerComponent implements AfterViewInit, OnDestroy {
  @ViewChild('scanner') scannerRef?: ElementRef<HTMLVideoElement>;
  readonly token = output<string | undefined>();
  readonly resultMatcher = input<string | undefined>(undefined);
  private readonly formats: BarcodeFormat[] = [BarcodeFormat.AZTEC];
  private readonly hints = new Map();
  private readonly codeReader: BrowserMultiFormatReader;
  private readonly scanner = signal<BrowserMultiFormatReader | null>(null);

  constructor() {
    this.hints.set(DecodeHintType.POSSIBLE_FORMATS, this.formats);
    this.codeReader = new BrowserMultiFormatReader(this.hints);
    this.scanner.set(this.codeReader);
  }

  ngAfterViewInit(): void {
    if (navigator.mediaDevices && this.scannerRef?.nativeElement) {
      navigator.mediaDevices
        .getUserMedia({ video: { facingMode: 'environment' } })
        .then(stream => {
          const video = this.scannerRef?.nativeElement;

          if (video) {
            video.setAttribute('playsinline', 'true'); // required to tell iOS Safari we don't want fullscreen
            video.srcObject = stream;
            video.play();

            // Start scanning after the video starts playing
            video.onloadedmetadata = () => {
              if (this.scannerRef?.nativeElement) {
                this.startScanning(this.codeReader);
              }
            };
          }
        })
        .catch(err => {
          alert('Error accessing the camera: ' + err);
        });
    } else {
      alert('No camera device present');
    }
  }

  startScanning(codeReader: BrowserMultiFormatReader) {
    if (this.scannerRef?.nativeElement) {
      codeReader.decodeFromVideoElementContinuously(this.scannerRef.nativeElement, (result, err) => {
        if (result) {
          const matcher = this.resultMatcher();
          if (matcher !== undefined) {
            if (result.getText().match(matcher)) {
              this.token.emit(result.getText());
            } else {
              console.warn('Detected token that did not match the required format.');
            }
          } else {
            this.token.emit(result.getText());
          }
          // If you want to stop scanning after a successful scan, uncomment the next line
          // codeReader.reset();
        }
        if (err && !(err instanceof NotFoundException)) {
          console.error(err);
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this.scanner()) {
      this.scanner()?.reset();
    }
  }
}
